import { useState, useEffect } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col, Card } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { announcementsApi, recordingsApi } from '../services/api'
import { DestinationSelector } from '../components/common'
import { useConfirm } from '../components/common/ConfirmDialog'

interface Announcement {
  id: string
  name: string
  description: string
  recording_id: string
  recording: {
    id: string
    name: string
    file_path: string
  } | null
  allow_skip: boolean
  repeat_count: number
  destination_type: string
  destination_id: string
  is_active: boolean
  created_at: string
  updated_at: string
}

interface Recording {
  id: string
  name: string
}

const emptyForm = {
  name: '',
  description: '',
  recording_id: '',
  allow_skip: false,
  repeat_count: 1,
  destination_type: '',
  destination_id: '',
  is_active: true
}

// Allowed destination types for announcements
const ANN_ALLOWED_TYPES = [
  'extension',
  'ring_group',
  'queue',
  'ivr',
  'time_condition',
  'conference',
  'voicemail',
  'parking',
  'night_mode',
  'announcement',
  'external_dest',
  'external',
  'hangup',
]

export default function Announcements() {
  const confirm = useConfirm()
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingAnn, setEditingAnn] = useState<Announcement | null>(null)
  const [formData, setFormData] = useState(emptyForm)
  const [recordings, setRecordings] = useState<Recording[]>([])

  useEffect(() => {
    loadAll()
  }, [])

  const loadAll = async () => {
    try {
      const [annRes, recRes] = await Promise.all([
        announcementsApi.list(),
        recordingsApi.list({ limit: 1000 }),
      ])
      setAnnouncements(annRes.data.items || [])
      setRecordings((recRes.data.items || []).map((r: Recording) => ({ id: r.id, name: r.name })))
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  const openCreateModal = () => {
    setEditingAnn(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (ann: Announcement) => {
    setEditingAnn(ann)
    setFormData({
      name: ann.name,
      description: ann.description || '',
      recording_id: ann.recording_id || '',
      allow_skip: ann.allow_skip,
      repeat_count: ann.repeat_count || 1,
      destination_type: ann.destination_type || '',
      destination_id: ann.destination_id || '',
      is_active: ann.is_active
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const data = {
        ...formData,
        recording_id: formData.recording_id || null,
        destination_id: formData.destination_id || null,
      }

      if (editingAnn) {
        await announcementsApi.update(editingAnn.id, data)
        toast.success('Ansage aktualisiert')
      } else {
        await announcementsApi.create(data)
        toast.success('Ansage erstellt')
      }
      setShowModal(false)
      loadAll()
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const handleDelete = async (ann: Announcement) => {
    const confirmed = await confirm({
      title: 'Ansage loschen',
      message: `Mochten Sie die Ansage "${ann.name}" wirklich loschen?`,
      confirmText: 'Loschen',
      cancelText: 'Abbrechen',
      variant: 'danger'
    })

    if (confirmed) {
      try {
        await announcementsApi.delete(ann.id)
        toast.success('Ansage geloscht')
        loadAll()
      } catch {
        toast.error('Fehler beim Loschen')
      }
    }
  }

  const getDestinationDisplay = (ann: Announcement) => {
    if (ann.destination_type === 'HANGUP') {
      return 'Auflegen'
    }
    if (ann.destination_type === 'EXTERNAL') {
      return `Extern: ${ann.destination_id}`
    }
    return `${ann.destination_type}: ${ann.destination_id || '-'}`
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Laden...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0">
            <i className="bi bi-megaphone me-2"></i>
            Ansagen
          </h2>
          <small className="text-muted">
            Ansage abspielen und dann zu einem Ziel weiterleiten
          </small>
        </div>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-circle me-2"></i>
          Neue Ansage
        </Button>
      </div>

      <Card>
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Aufnahme</th>
                <th>Ziel</th>
                <th>Optionen</th>
                <th>Status</th>
                <th className="text-end">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {announcements.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    Keine Ansagen konfiguriert
                  </td>
                </tr>
              ) : (
                announcements.map((ann) => (
                  <tr key={ann.id}>
                    <td>
                      <strong>{ann.name}</strong>
                      {ann.description && (
                        <div className="small text-muted">{ann.description}</div>
                      )}
                    </td>
                    <td>
                      {ann.recording ? (
                        <span>
                          <i className="bi bi-file-music me-1"></i>
                          {ann.recording.name}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{getDestinationDisplay(ann)}</td>
                    <td>
                      {ann.repeat_count > 1 && (
                        <Badge bg="secondary" className="me-1">
                          {ann.repeat_count}x
                        </Badge>
                      )}
                      {ann.allow_skip && (
                        <Badge bg="info">
                          <i className="bi bi-skip-forward me-1"></i>
                          Skip
                        </Badge>
                      )}
                    </td>
                    <td>
                      <Badge bg={ann.is_active ? 'success' : 'secondary'}>
                        {ann.is_active ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </td>
                    <td className="text-end">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-1"
                        onClick={() => openEditModal(ann)}
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(ann)}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingAnn ? 'Ansage bearbeiten' : 'Neue Ansage erstellen'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Aufnahme</Form.Label>
                  <Form.Select
                    value={formData.recording_id}
                    onChange={(e) => setFormData({ ...formData, recording_id: e.target.value })}
                  >
                    <option value="">-- Keine Aufnahme --</option>
                    {recordings.map((rec) => (
                      <option key={rec.id} value={rec.id}>
                        {rec.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Aufnahme wird vor der Weiterleitung abgespielt
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Beschreibung</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Wiederholungen</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    max={10}
                    value={formData.repeat_count}
                    onChange={(e) => setFormData({ ...formData, repeat_count: parseInt(e.target.value) || 1 })}
                  />
                  <Form.Text className="text-muted">
                    Wie oft soll die Ansage wiederholt werden?
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Optionen</Form.Label>
                  <div className="mt-2">
                    <Form.Check
                      type="checkbox"
                      label="Uberspringen erlauben (Tastendruck)"
                      checked={formData.allow_skip}
                      onChange={(e) => setFormData({ ...formData, allow_skip: e.target.checked })}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <hr />

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Ziel nach der Ansage *</Form.Label>
                  <DestinationSelector
                    destinationType={formData.destination_type}
                    destinationValue={formData.destination_id}
                    onChange={(type, value) => setFormData({
                      ...formData,
                      destination_type: type,
                      destination_id: value
                    })}
                    allowedTypes={ANN_ALLOWED_TYPES}
                    allowExternal={true}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="is_active"
                    label="Aktiv"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Abbrechen
            </Button>
            <Button variant="primary" type="submit">
              {editingAnn ? 'Speichern' : 'Erstellen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}
